import api from './api';
import { authManager } from './authManager';

export interface LogLevel {
  current: string;
  available: string[];
}

export interface LogFile {
  name: string;
  size: number;
  modified: string;
}

export interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  service?: string;
  meta?: Record<string, any>;
}

export interface LogSearchResult {
  entries: LogEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SearchParams {
  file: string;
  pattern?: string;
  level?: string;
  page?: number;
  pageSize?: number;
}

export const monitoringService = {
  /**
   * Get current log level and available levels
   */
  async getLogLevel(): Promise<LogLevel> {
    const response = await api.get('/monitoring/log-level');
    return response.data.data;
  },

  /**
   * Set log level dynamically
   */
  async setLogLevel(level: string): Promise<{ success: boolean; level: string; message: string }> {
    const response = await api.post('/monitoring/log-level', { level });
    return response.data.data;
  },

  /**
   * Get list of available log files
   */
  async listLogFiles(): Promise<LogFile[]> {
    const response = await api.get('/monitoring/logs/files');
    return response.data.data;
  },

  /**
   * Search log files
   */
  async searchLogs(params: SearchParams): Promise<LogSearchResult> {
    const response = await api.post('/monitoring/logs/search', params);
    return response.data.data;
  },

  /**
   * Get download URL for a log file
   */
  getDownloadUrl(filename: string): string {
    const token = authManager.getCurrentToken();
    return `/api/v1/monitoring/logs/download/${encodeURIComponent(filename)}?token=${encodeURIComponent(token || '')}`;
  },

  /**
   * Get SSE stream URL for live logs
   */
  getStreamUrl(options?: { minLevel?: string; filter?: string }): string {
    const token = authManager.getCurrentToken();
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (options?.minLevel) params.set('minLevel', options.minLevel);
    if (options?.filter) params.set('filter', options.filter);
    return `/api/v1/monitoring/logs/stream?${params.toString()}`;
  },
};
